import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import { apiRequest } from 'lib/api';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
const ChatAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showWelcomeForm, setShowWelcomeForm] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [formErrors, setFormErrors] = useState({});
  const [chatStarted, setChatStarted] = useState(false);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load user info from localStorage on mount
  useEffect(() => {
    const savedUserInfo = localStorage.getItem('nikunjChatUserInfo');
    if (savedUserInfo) {
      const userData = JSON.parse(savedUserInfo);
      setUserInfo(userData);
      setShowWelcomeForm(false);
      setChatStarted(true);
      
      // Add welcome back message
      setMessages([{
        id: Date.now(),
        text: `Welcome back, ${userData.name}! I'm Nikunj. How can I help you today?`,
        sender: 'agent',
        timestamp: new Date().toISOString()
      }]);
    }
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const userData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      timestamp: new Date().toISOString()
    };

    // Save to localStorage
    localStorage.setItem('nikunjChatUserInfo', JSON.stringify(userData));
    setUserInfo(userData);
    setShowWelcomeForm(false);
    setChatStarted(true);

    // Add welcome message
    setMessages([{
      id: Date.now(),
      text: `Hi ${userData.name}! I'm Nikunj. I'm here to answer questions about Nikunj's experience, projects, and expertise across his three professional worlds: Community Building, Web3 Development, and Software Engineering. What would you like to know?`,
      sender: 'agent',
      timestamp: new Date().toISOString()
    }]);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: newMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsLoading(true);

    try {
      // Call the chat API
      const response = await apiRequest('/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: newMessage,
          userInfo: userInfo,
          conversationHistory: messages.slice(-10) // Send last 10 messages for context
        })
      });

      if (!response.success) {
        throw new Error('Failed to get response');
      }
      
      const agentMessage = {
        id: Date.now() + 1,
        text: response.response,
        sender: 'agent',
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, agentMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "I'm sorry, I'm having trouble responding right now. Please try again in a moment.",
        sender: 'agent',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    localStorage.removeItem('nikunjChatUserInfo');
    setUserInfo(null);
    setShowWelcomeForm(true);
    setChatStarted(false);
    setFormData({ name: '', email: '' });
    setFormErrors({});
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-primary to-accent text-surface rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", duration: 0.5, delay: 1 }}
      >
        <Icon 
          name={isOpen ? "X" : "MessageCircle"} 
          size={24} 
          className="mx-auto" 
        />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-6 w-96 h-[600px] bg-surface border border-border/20 rounded-xl shadow-2xl z-40 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-accent text-surface p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-surface/20 flex items-center justify-center">
                  <Icon name="User" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold">Chat with Nikunj</h3>
                  <p className="text-xs opacity-90">AI Assistant</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {chatStarted && (
                  <button
                    onClick={clearChat}
                    className="p-2 hover:bg-surface/20 rounded-lg transition-colors"
                    title="Start New Chat"
                  >
                    <Icon name="RotateCcw" size={16} />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-surface/20 rounded-lg transition-colors"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>

            {/* Welcome Form */}
            {showWelcomeForm && (
              <div className="flex-1 p-6 flex flex-col justify-center">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center">
                    <Icon name="MessageCircle" size={24} className="text-surface" />
                  </div>
                  <h3 className="text-xl font-bold text-primary mb-2">
                    Welcome to Nikunj's AI Assistant!
                  </h3>
                  <p className="text-text-secondary text-sm">
                    I can answer questions about Nikunj's experience, projects, and expertise. 
                    Please share your details to get started.
                  </p>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        formErrors.name ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="Enter your name"
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                        formErrors.email ? 'border-red-500' : 'border-border'
                      }`}
                      placeholder="Enter your email"
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-primary to-accent text-surface py-2 rounded-lg font-medium hover:opacity-90 transition-opacity"
                  >
                    Start Chatting
                  </button>
                </form>

                <p className="text-xs text-text-secondary mt-4 text-center">
                  Your information is only used to personalize the chat experience.
                </p>
              </div>
            )}

            {/* Chat Messages */}
            {chatStarted && (
              <>
                <div 
                  ref={chatContainerRef}
                  className="flex-1 p-4 overflow-y-auto space-y-4"
                  style={{ maxHeight: 'calc(600px - 140px)' }}
                >
                  {messages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        message.sender === 'user'
                          ? 'bg-gradient-to-r from-primary to-accent text-surface'
                          : message.isError
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-background text-text-primary border border-border/20'
                      }`}>
                        <div className="text-sm">
                          {message.sender === 'user' ? (
                            <p className="whitespace-pre-wrap">{message.text}</p>
                          ) : (
                            <Markdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                code(props) {
                                  const {children, className, node, ...rest} = props
                                  const match = /language-(\w+)/.exec(className || '')
                                  return match ? (
                                    <SyntaxHighlighter
                                      {...rest}
                                      PreTag="div"
                                      children={String(children).replace(/\n$/, '')}
                                      language={match[1]}
                                      style={vscDarkPlus}
                                      customStyle={{
                                        margin: 0,
                                        borderRadius: '0.375rem',
                                        fontSize: '0.875rem'
                                      }}
                                    />
                                  ) : (
                                    <code {...rest} className={`${className} bg-gray-100 px-1 py-0.5 rounded text-sm font-mono`}>
                                      {children}
                                    </code>
                                  )
                                },
                                p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                                h1: ({children}) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                                h2: ({children}) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                                h3: ({children}) => <h3 className="text-sm font-bold mb-1">{children}</h3>,
                                ul: ({children}) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                                ol: ({children}) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                                li: ({children}) => <li className="text-sm">{children}</li>,
                                blockquote: ({children}) => (
                                  <blockquote className="border-l-4 border-gray-300 pl-4 italic my-2">
                                    {children}
                                  </blockquote>
                                ),
                                a: ({href, children}) => (
                                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    {children}
                                  </a>
                                ),
                                table: ({children}) => (
                                  <div className="overflow-x-auto my-2">
                                    <table className="min-w-full border border-gray-300 text-sm">
                                      {children}
                                    </table>
                                  </div>
                                ),
                                thead: ({children}) => <thead className="bg-gray-50">{children}</thead>,
                                th: ({children}) => <th className="border border-gray-300 px-2 py-1 text-left font-medium">{children}</th>,
                                td: ({children}) => <td className="border border-gray-300 px-2 py-1">{children}</td>,
                              }}
                            >
                              {message.text}
                            </Markdown>
                          )}
                        </div>
                        <p className={`text-xs mt-1 opacity-70 ${
                          message.sender === 'user' ? 'text-surface' : 'text-text-secondary'
                        }`}>
                          {formatTimestamp(message.timestamp)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Typing Indicator */}
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex justify-start"
                    >
                      <div className="bg-background text-text-primary border border-border/20 p-3 rounded-lg">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-border/20">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      className="flex-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                      disabled={isLoading}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || isLoading}
                      className="px-4 py-2 bg-gradient-to-r from-primary to-accent text-surface rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon name="Send" size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatAgent;
